import React, { useState } from 'react';

export default function TrendingAnalysis({ user, onCreditsUpdate }) {
  const [topic, setTopic] = useState('');
  const [region, setRegion] = useState('IN');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/analysis/trending-keywords', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ topic: topic.trim(), region })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || "Failed to fetch trending analysis.");
      }

      if (data.success && data.data) {
        setResults(data.data);
        if (data.credits?.remaining !== undefined && onCreditsUpdate) {
          onCreditsUpdate(data.credits.remaining);
        }
      } else {
        throw new Error(data.message || "No trending data found.");
      }

    } catch (err) {
      console.error("Trending Search Error:", err);
      setError(err.message || "Failed to load trending data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="analysis-container" style={{ padding: '20px', maxWidth: '1300px', margin: '0 auto', color: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Header Section */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontSize: '30px', margin: '0 0 10px 0', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '10px' }}>
          🔥 Trending Keywords & Topic Scanner
        </h2>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: '15px' }}>
          Discover hot viral topics, regional keyword volume, and AI content opportunities for specific topics or country trends.
        </p>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap', background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ flex: '1 1 300px' }}>
          <input 
            type="text" 
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Enter topic keyword (e.g. AI SaaS, Stock Market) or leave empty for general country trends..."
            style={{ 
              width: '100%', 
              padding: '16px 20px', 
              borderRadius: '12px', 
              border: '1px solid rgba(255, 255, 255, 0.12)', 
              background: 'rgba(0, 0, 0, 0.3)', 
              color: '#ffffff',
              fontSize: '15px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ width: '140px' }}>
          <select 
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '16px', 
              borderRadius: '12px', 
              border: '1px solid rgba(255, 255, 255, 0.12)', 
              background: '#0f111a', 
              color: '#ffffff',
              fontSize: '15px',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="IN">🇮🇳 India</option>
            <option value="US">🇺🇸 USA</option>
            <option value="GB">🇬🇧 UK</option>
            <option value="CA">🇨🇦 Canada</option>
            <option value="AU">🇦🇺 Australia</option>
          </select>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          style={{ 
            padding: '16px 30px', 
            borderRadius: '12px', 
            background: loading ? '#334155' : 'linear-gradient(90deg, #8b5cf6, #ec4899)', 
            color: 'white', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '15px',
            minWidth: '200px',
            transition: 'all 0.3s',
            boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
          }}
        >
          {loading ? '⏳ Scanning Trends...' : (topic.trim() ? 'Scan Topic Trends' : 'Scan Country Trends')}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div style={{ background: 'rgba(239, 68, 68, 0.15)', borderLeft: '4px solid #ef4444', color: '#fca5a5', padding: '16px', borderRadius: '10px', marginBottom: '30px', fontSize: '15px' }}>
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '25px', animation: 'fadeIn 0.5s ease-in' }}>
          
          {/* AI Analysis Content Gap Box */}
          {(results.aiAnalysis || (results.topKeywords && results.topKeywords.length > 0)) && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', padding: '24px', borderRadius: '16px', borderLeft: '4px solid #f59e0b', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
              <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#f8fafc', fontSize: '18px' }}>
                🤖 AI Content Opportunities & Hot Keywords ({results.topic || region})
              </h3>
              {results.aiAnalysis && (
                <div style={{ color: '#cbd5e1', lineHeight: '1.6', fontSize: '15px', whiteSpace: 'pre-wrap', background: 'rgba(0, 0, 0, 0.3)', padding: '18px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', marginBottom: '15px' }}>
                  {results.aiAnalysis}
                </div>
              )}
              {results.topKeywords && results.topKeywords.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '14px', color: '#94a3b8', margin: '0 0 10px 0' }}>🔥 Top Trending Keywords:</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {results.topKeywords.map((kw, i) => (
                      <span key={i} style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', border: '1px solid rgba(139, 92, 246, 0.3)', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>
                        #{kw.keyword} ({kw.frequency}x)
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Top Trending Videos List */}
          {results.trendingVideos && results.trendingVideos.length > 0 && (
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
              <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '12px', color: '#f8fafc', fontSize: '18px' }}>
                📹 Top Trending Videos ({results.topic !== 'General Trending' ? results.topic : region})
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {results.trendingVideos.map((video, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '16px', background: 'rgba(0, 0, 0, 0.3)', padding: '16px', borderRadius: '12px', alignItems: 'center', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    
                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#ec4899', minWidth: '40px', textAlign: 'center' }}>
                      #{idx + 1}
                    </div>
                    
                    <img 
                      src={
                        video.thumbnails?.medium?.url || 
                        video.thumbnails?.high?.url || 
                        video.thumbnails?.default?.url || 
                        `https://i.ytimg.com/vi/${video.videoId}/mqdefault.jpg`
                      } 
                      alt={video.title} 
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/120x90?text=No+Thumb' }}
                      style={{ width: '120px', height: '90px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} 
                    />
                    
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', lineHeight: '1.4' }}>
                        <a href={`https://youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noreferrer" style={{ color: '#f8fafc', textDecoration: 'none', fontWeight: '600' }}>
                          {video.title}
                        </a>
                      </h4>
                      
                      <div style={{ display: 'flex', gap: '15px', fontSize: '13px', color: '#94a3b8' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          📺 <strong style={{color: '#cbd5e1', fontWeight: '600'}}>{video.channelTitle || video.channel}</strong>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          👀 <strong style={{color: '#cbd5e1'}}>{Number(video.viewCount || video.views || 0).toLocaleString()} views</strong>
                        </span>
                      </div>
                    </div>
                    
                    <a 
                      href={`https://youtube.com/watch?v=${video.videoId}`} 
                      target="_blank" 
                      rel="noreferrer"
                      style={{ padding: '8px 16px', background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: '600', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                    >
                      Watch ↗
                    </a>

                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
